/**
 * @module app.um
 */

import { autoinject, bindingMode, observable } from 'aurelia-framework';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { bindable } from 'aurelia-typed-observable-plugin';
import { apiCall } from 'app/shared/lib/essette-api';
import { Toaster } from 'app/shared/lib/toaster';
import { AuthService, IAuthStatusSearchFilters } from 'app/um/services/auth-service';
import { getReference } from 'app/shared/models/essette-types';
import { I18N } from 'aurelia-i18n';
import { FlagOptions } from 'app/um/components/medication-statuses/medication-statuses';
import { deepClone } from 'app/shared/lib/utilities';
import { guid } from 'app/shared/lib/guid';
import * as UM from 'app/um/models/essette-api';

enum EventOptions {
    initialStatus = 'Initial Status'
}

@autoinject()
export class SaveMedicationStatus extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.toView, changeHandler: 'reset' })
    public authMedicationStatus: UM.AuthMedicationStatus = { id: null };

    @bindable.array({ defaultBindingMode: bindingMode.toView })
    protected statuses: UM.AuthMedicationStatus[] = [];

    @bindable.array({ defaultBindingMode: bindingMode.toView })
    protected determinationStatuses: UM.AuthStatus[] = [];

    @bindable.array({ defaultBindingMode: bindingMode.toView })
    protected flagOptions: Array<string> = [];

    @observable
    private addDetermination: boolean = false;

    private authMedicationStatusId: string | null = null;
    // Value Bindings
    protected status: string = '';
    protected code: string = '';
    protected determinationStatus: UM.AuthStatus | null = null;
    protected flags: Array<string> = [];
    protected transitions: UM.AuthMedicationStatus[] = [];
    protected exclusiveEvent: string | null = null;
    protected isInitialStatus: boolean = false;
    protected providerChangesRequestedEvent: boolean = false;
    protected id: guid = guid();

    // Drop Down Options
    private eventOptions: string[] = [];

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly service: AuthService,
        private readonly toaster: Toaster,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.eventOptions = <string[]>Object.values(EventOptions);

        this.reset();
    }

    ////****************** Events ***************************************************////
    protected addDeterminationChanged(): void {
        if(!this.addDetermination && this.determinationStatus) {
            this.determinationStatus = null;
        }
    }

    ////****************** Functions ************************************************////
    // Build Auth Medication Status to send over to the api
    protected buildAuthMedicationStatus(): UM.AuthMedicationStatus {
        let isApproval: boolean = false, isOpen: boolean = false, isVoided: boolean = false;

        this.flags.forEach(flag => {
            switch(flag) {
                case FlagOptions.isOpen:
                    isOpen = true;
                    break;
                case FlagOptions.isVoided:
                    isVoided = true;
                    break;
            }
        });

        const returnTransitions = <Array<UM.AuthMedicationStatus>>this.transitions.map(t => getReference(t));

        const returnAuthMedicationStatus: UM.AuthMedicationStatus = {
            id: this.authMedicationStatus.id,
            description: this.status,
            code: this.code,
            determination: {
                isDetermination: this.addDetermination
            },
            flag: {
                // Status Flags
                isApproval: isApproval,
                isOpen: isOpen,
                isVoided: isVoided
            },
            transitions: returnTransitions
        };
        if(this.addDetermination) returnAuthMedicationStatus.determination = {
            ...returnAuthMedicationStatus.determination,
            status: getReference(this.determinationStatus)
        };

        return returnAuthMedicationStatus;
    }

    // Build Auth Medication Status to send over to the api, exclusive to just status events
    protected buildAuthMedicationStatusExclusive(): UM.AuthMedicationStatus {
        const returnAuthMedicationStatus: UM.AuthMedicationStatus = {
            id: this.authMedicationStatusId
        };

        switch(this.exclusiveEvent) {
            case EventOptions.initialStatus:
                returnAuthMedicationStatus.flag = {
                    isInitial: true
                };
                break;
        }

        // if no event selected but status once was the Initial Medication Status, clear it out
        if(!this.exclusiveEvent && this.isInitialStatus) {
            returnAuthMedicationStatus.flag = {
                isInitial: false
            };
        }

        return returnAuthMedicationStatus;
    }

    protected buildAuthMedicationStatusEventsProviderChangesRequested(): UM.AuthMedicationStatus {
        const returnAuthMedicationStatus: UM.AuthMedicationStatus = {
            id: this.authMedicationStatusId,
            flag: {
                providerChangesRequested: true
            }
        };

        return returnAuthMedicationStatus;
    }

    ////****************** IForm ****************************************************////

    public async reset(): Promise<void> {
        this.authMedicationStatusId = this.authMedicationStatus.id ?? null;
        this.status = this.authMedicationStatus.description || '';
        this.code = this.authMedicationStatus.code || '';
        this.addDetermination = !!this.authMedicationStatus.determination?.isDetermination;
        this.determinationStatus = this.authMedicationStatus.determination?.status || null;
        if(this.addDetermination && !this.determinationStatus && this.determinationStatuses.length > 0) this.determinationStatus = this.determinationStatuses[0]; // first entry is 'None'
        this.flags = [];
        this.transitions = [];
        this.exclusiveEvent = null;
        this.providerChangesRequestedEvent = false;

        const flags = this.authMedicationStatus.flag;
        if(flags) {
            // Set Flags
            this.flagOptions.forEach(option => {
                if(option === FlagOptions.isOpen && flags.isOpen) {
                    this.flags.push(option);
                }
                if(option === FlagOptions.isVoided && flags.isVoided) {
                    this.flags.push(option);
                }
            });

            // Set Events
            if(flags.isInitial) {
                this.exclusiveEvent = EventOptions.initialStatus;
            }
            if(flags.providerChangesRequested) {
                this.providerChangesRequestedEvent = true;
            }
        }

        this.transitions = this.authMedicationStatus.transitions ? deepClone(this.authMedicationStatus.transitions) : [];
    }

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        let isSuccessful = false;

        try {
            this.state.beginSave();

            // If an the Initial Status Event was deselected, we need to !updateExclusive first
            // to satisfy a potential addDetermination check constraint error
            if(!this.exclusiveEvent && this.isInitialStatus) {
                isSuccessful = await this.updateAuthMedicationStatusEvents();
                this.isInitialStatus = false;
            }

            // Save Auth Medication Status
            isSuccessful = await this.saveAuthMedicationStatus();

            // If an event was selected, push an exclusive update
            if(this.exclusiveEvent || this.providerChangesRequestedEvent) {
                isSuccessful = await this.updateAuthMedicationStatusEvents();
            }
        }
        catch(error) {
            throw error;
        }
        finally {
            this.toaster.standard.saved('um:medication.status.$long', isSuccessful);
            this.state.endSave();
            return isSuccessful;
        }
    }

    ////****************** Service Calls ********************************************////

    // To Add/Update an Auth Medication Status
    @apiCall
    protected async saveAuthMedicationStatus(): Promise<boolean> {
        let isSuccessful = false;
        try {
            const response = await this.service.saveMedicationStatus(this.buildAuthMedicationStatus());
            if(!this.authMedicationStatusId) {
                this.authMedicationStatusId = response.id ?? null;
            }
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            // If Duplicate Status Code constraint violated, show toast
            if(error.status.errors) {
                const invalidCode = error.status.errors.find((err: any) => err.code === 'property.constraintViolated' && err.property === 'code');

                if(invalidCode) {
                    this.toaster.error().message(this.i18n.tr('ui:messages.duplicate.save_error', { what: this.i18n.tr('um:medication.status.code.$long'), item: this.i18n.tr('um:medication.status.$long') })).show();
                }
            }

            throw error;
        }
        finally {
            return isSuccessful;
        }
    }

    // To Update an Auth Medication Status's Event information
    @apiCall
    protected async updateAuthMedicationStatusEvents(): Promise<boolean> {
        let isSuccessful = false;
        if(this.authMedicationStatusId) {
            try {
                const promises = [];
                if(this.exclusiveEvent) {
                    promises.push(this.service.updateMedicationStatusExclusive(this.buildAuthMedicationStatusExclusive()));
                }
                if(this.providerChangesRequestedEvent) {
                    promises.push(this.service.updateMedicationStatusExclusive(this.buildAuthMedicationStatusEventsProviderChangesRequested()));
                }
                const responses = await Promise.all(promises);
                isSuccessful = responses.every((response: any) => response.isSuccessful);
            }
            catch(error) {
                throw error;
            }
            finally {
                this.toaster.standard.saved('um:medication.status.$long', isSuccessful);
            }
        }
        return isSuccessful;
    }
}
